// Cleanor Search Index — typed access to the bundled study datasets.
//
// The JSON is SYNCED (npm run trends:sync) from the open-source source of truth
// github.com/cleanor-app/search-index, then committed here so the site builds
// without the sibling repo (Cloudflare Workers has no runtime filesystem, so
// these are build-time imports, not public/ fetches).
//
// Bundle split: the catalog hub only imports the tiny `summary.json`; each study
// page dynamically imports ONLY its own dataset (a separate chunk), so pages
// never ship datasets they do not render. To add a study: add its category to
// the search-index repo, build + `npm run trends:sync`, then add one entry to
// `presentation` and one line to `datasetLoaders` below.

import summaryData from '@/data/popularity/summary.json';

export type PopularityBrandRow = {
  name: string;
  vendor: string;
  competition: string;
  avg: number;
  monthly: number[];
  mom: number | null;
  momentum: number | null; // smoothed 3-month change (robust to KP's rounded bands)
  yoy: number | null;
  rank: number;
  share: number;
  reliable: boolean; // above a volume floor, so tiny-base swings never headline movers
};

export type PopularityScopeStat = {
  hhi: number; // 0..1 concentration (sum of squared shares); 1 = monopoly
  total: number;
  leader: string | null;
  leaderShare: number | null;
};

export type PopularityDataset = {
  category: string;
  label: string;
  unit: string;
  snapshot: string;
  updated: string;
  source: string;
  sourceNote: string;
  methodologyUrl?: string;
  months: string[];
  countries: { code: string; name: string }[];
  brands: { name: string; vendor: string; keys: string[] }[];
  stats: Record<string, PopularityScopeStat>;
  series: Record<string, PopularityBrandRow[]>;
};

// Lightweight per-study summary (no series) — all the catalog hub needs.
export type StudySummary = {
  category: string;
  label: string;
  unit: string;
  brands: number;
  through: string;
  updated: string;
  leader: { name: string; share: number };
  total: number;
  hhi: number | null;
};

// How each study is presented (its dataset is loaded on demand, not here).
export type StudyMeta = {
  slug: string;
  category: string;
  noun: string;
  question: string;
  blurb: string;
  kicker: string;
  accent: string;
  emoji: string;
  keywords: string[];
};

export type Study = StudyMeta & { summary: StudySummary };

const presentation: StudyMeta[] = [
  {
    slug: 'ai-tools',
    category: 'ai',
    noun: 'AI tools',
    question: 'How popular are AI tools, really?',
    blurb:
      'ChatGPT, Gemini, Claude, Grok and the rest, ranked by real Google search demand worldwide and by country.',
    kicker: 'AI systems',
    accent: '#4285f4',
    emoji: '🤖',
    keywords: [
      'most popular ai tools',
      'ai chatbot popularity',
      'chatgpt vs gemini vs claude searches',
      'ai search demand by country',
      'ai tool market share',
    ],
  },
  {
    slug: 'file-formats',
    category: 'formats',
    noun: 'file formats',
    question: 'Which file formats do people actually search for?',
    blurb:
      'PDF, JPG, PNG, WebP, HEIC, SVG and more, ranked by real Google search demand worldwide and by country.',
    kicker: 'File formats',
    accent: '#0f9d58',
    emoji: '🗂️',
    keywords: [
      'most popular file formats',
      'jpg vs png vs webp popularity',
      'image format search demand',
      'file format market share',
      'webp vs heic vs avif',
    ],
  },
  {
    slug: 'design-tools',
    category: 'design',
    noun: 'design tools',
    question: 'Which design tools do people search for?',
    blurb:
      'Canva, Figma, Photoshop, Procreate, Adobe Illustrator and more, ranked by real Google search demand worldwide and by country.',
    kicker: 'Design tools',
    accent: '#c026d3',
    emoji: '🎨',
    keywords: [
      'most popular design tools',
      'best design tool',
      'figma vs canva vs photoshop',
      'ui design tool popularity',
      'most searched design software',
    ],
  },
  {
    slug: 'no-code-tools',
    category: 'nocode',
    noun: 'no-code tools',
    question: 'Which no-code tools do people search for?',
    blurb:
      'Airtable, Zapier, n8n, Webflow, Bubble, Retool and more, ranked by real Google search demand worldwide and by country.',
    kicker: 'No-code tools',
    accent: '#4f46e5',
    emoji: '🧩',
    keywords: [
      'most popular no-code tools',
      'best no-code platform',
      'zapier vs make vs n8n',
      'no-code app builder popularity',
      'most searched no code tool',
    ],
  },
  {
    slug: 'seo-tools',
    category: 'seo',
    noun: 'SEO tools',
    question: 'Which SEO tools do people search for?',
    blurb:
      'Google Search Console, Semrush, Ahrefs, Moz, Screaming Frog and more, ranked by real Google search demand worldwide and by country.',
    kicker: 'SEO tools',
    accent: '#db2777',
    emoji: '🔍',
    keywords: [
      'most popular seo tools',
      'best seo tool',
      'semrush vs ahrefs vs moz',
      'seo tool search demand',
      'most searched seo software',
    ],
  },
  {
    slug: 'ai-coding-tools',
    category: 'coding',
    noun: 'AI coding tools and IDEs',
    question: 'Which code editors and AI coding tools do people search for?',
    blurb:
      'Cursor, GitHub Copilot, Claude Code, VS Code, Replit and more, ranked by real Google search demand worldwide and by country.',
    kicker: 'Coding tools',
    accent: '#0891b2',
    emoji: '🧑‍💻',
    keywords: [
      'most popular ai coding tools',
      'best code editor',
      'cursor vs copilot vs vs code',
      'ai coding assistant popularity',
      'most searched ide',
    ],
  },
  {
    slug: 'crypto',
    category: 'crypto',
    noun: 'crypto exchanges and wallets',
    question: 'Which crypto exchanges and wallets do people search for?',
    blurb:
      'Coinbase, Binance, MEXC, Kraken, MetaMask, Trust Wallet and more, ranked by real Google search demand worldwide and by country.',
    kicker: 'Crypto platforms',
    accent: '#f7931a',
    emoji: '🪙',
    keywords: [
      'most popular crypto exchange',
      'best crypto exchange',
      'coinbase vs binance vs kraken popularity',
      'crypto wallet search demand by country',
      'crypto exchange market share',
    ],
  },
  {
    slug: 'cloud-storage',
    category: 'storage',
    noun: 'cloud storage services',
    question: 'Which cloud storage do people search for?',
    blurb:
      'Google Drive, iCloud, Dropbox, OneDrive, Terabox and more, ranked by real Google search demand worldwide and by country.',
    kicker: 'Cloud storage',
    accent: '#00897b',
    emoji: '☁️',
    keywords: [
      'most popular cloud storage',
      'best cloud storage',
      'google drive vs dropbox vs onedrive',
      'cloud storage search demand by country',
      'cloud storage market share',
    ],
  },
  {
    slug: 'apps',
    category: 'apps',
    noun: 'consumer apps',
    question: 'Which apps are people actually searching for?',
    blurb:
      'YouTube, WhatsApp, Instagram, TikTok, Spotify and more, ranked by real Google search demand worldwide and by country.',
    kicker: 'Consumer apps',
    accent: '#ea4335',
    emoji: '📱',
    keywords: [
      'most popular apps',
      'most searched apps',
      'whatsapp vs instagram vs tiktok popularity',
      'app search demand by country',
      'fastest growing apps 2026',
    ],
  },
  {
    slug: 'programming-languages',
    category: 'languages',
    noun: 'programming languages',
    question: 'Which programming languages are people searching for?',
    blurb:
      'Python, JavaScript, Java, TypeScript, Rust, Go and more, ranked by real Google search demand worldwide and by country.',
    kicker: 'Programming languages',
    accent: '#a142f4',
    emoji: '⌨️',
    keywords: [
      'most popular programming languages',
      'python vs javascript popularity',
      'programming language search demand',
      'programming language ranking by country',
      'most searched programming language',
    ],
  },
];

// Per-category dynamic import — one chunk each, loaded only by that study page.
const datasetLoaders: Record<string, () => Promise<{ default: PopularityDataset }>> = {
  ai: () => import('@/data/popularity/ai.json') as Promise<{ default: PopularityDataset }>,
  apps: () => import('@/data/popularity/apps.json') as Promise<{ default: PopularityDataset }>,
  crypto: () => import('@/data/popularity/crypto.json') as Promise<{ default: PopularityDataset }>,
  coding: () => import('@/data/popularity/coding.json') as Promise<{ default: PopularityDataset }>,
  seo: () => import('@/data/popularity/seo.json') as Promise<{ default: PopularityDataset }>,
  nocode: () => import('@/data/popularity/nocode.json') as Promise<{ default: PopularityDataset }>,
  design: () => import('@/data/popularity/design.json') as Promise<{ default: PopularityDataset }>,
  storage: () =>
    import('@/data/popularity/storage.json') as Promise<{ default: PopularityDataset }>,
  formats: () =>
    import('@/data/popularity/formats.json') as Promise<{ default: PopularityDataset }>,
  languages: () =>
    import('@/data/popularity/languages.json') as Promise<{ default: PopularityDataset }>,
};

const summaries = summaryData as StudySummary[];
const summaryByCategory = Object.fromEntries(summaries.map((s) => [s.category, s]));

export const studies: Study[] = presentation
  .filter((p) => summaryByCategory[p.category])
  .map((p) => ({ ...p, summary: summaryByCategory[p.category] }));

export const studyBySlug: Record<string, Study> = Object.fromEntries(
  studies.map((s) => [s.slug, s]),
);

export async function loadDataset(category: string): Promise<PopularityDataset | null> {
  const loader = datasetLoaders[category];
  if (!loader) return null;
  return (await loader()).default;
}

// Human label for a "YYYY-MM" month key, e.g. "2026-05" -> "May 2026".
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const names = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${names[(m || 1) - 1]} ${y}`;
}

export function formatVolume(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}

// A plain-English label for an HHI concentration value.
export function concentrationLabel(hhi: number | null): string {
  if (hhi == null) return '';
  if (hhi >= 0.6) return 'a near-monopoly';
  if (hhi >= 0.25) return 'highly concentrated';
  if (hhi >= 0.15) return 'concentrated';
  return 'competitive';
}
